import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Globe, DollarSign, LogOut, LayoutDashboard, Heart, MessageCircle, KeyRound, Shield } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, setLang, currency, setCurrency, t } = useLocale();
  const navigate = useNavigate();
  const [unread, setUnread] = React.useState(0);

  React.useEffect(() => {
    if (!user) { setUnread(0); return; }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await (await import("@/lib/api")).api.get("/conversations/unread-count");
        if (!cancelled) setUnread(data.count || 0);
      } catch (err) {
        // silent
      }
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${isActive ? "text-primary font-semibold" : "text-foreground/70 hover:text-primary"}`;

  return (
    <header className="sticky top-0 z-40 glass-nav">
      <div className="mx-auto max-w-7xl px-4 md:px-10 py-3 md:py-4 flex items-center gap-4 md:gap-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0" aria-label="To2casa">
          <Logo variant="full" className="h-10 md:h-12 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-8 flex-1">
          <NavLink to="/properties" className={linkCls} data-testid="nav-properties">
            {t("nav.properties")}
          </NavLink>
          <NavLink to="/projects" className={linkCls} data-testid="nav-projects">
            <span className="inline-flex items-center gap-1.5">
              {t("nav.projects")}
              <span className="text-[9px] uppercase tracking-widest bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded-sm font-semibold">
                {t("projects.badge_new")}
              </span>
            </span>
          </NavLink>
          <NavLink to="/noticias" className={linkCls} data-testid="nav-news">
            {t("nav.news")}
          </NavLink>
          {user && user.role === "buscador" && (
            <NavLink to="/favorites" className={linkCls} data-testid="nav-favorites">
              {t("nav.favorites")}
            </NavLink>
          )}
          {user && user.role === "agente" && (
            <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">
              {t("nav.dashboard")}
            </NavLink>
          )}
          {user && user.role === "admin" && (
            <NavLink to="/admin/news" className={linkCls} data-testid="nav-admin-news">
              Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-1.5 md:gap-3 ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-toggle"
                className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 md:px-3 py-1.5 text-[10px] md:text-xs uppercase tracking-widest hover:bg-muted transition-colors"
              >
                <Globe className="h-3.5 w-3.5" />
                {lang}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLang("es")} data-testid="lang-es">Español</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">English</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="currency-toggle"
                className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 md:px-3 py-1.5 text-[10px] md:text-xs uppercase tracking-widest hover:bg-muted transition-colors"
              >
                <DollarSign className="h-3.5 w-3.5" />
                {currency}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setCurrency("USD")} data-testid="currency-usd">USD $</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setCurrency("DOP")} data-testid="currency-dop">DOP RD$</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <>
              <NavLink
                to="/messages"
                className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-border hover:bg-muted transition-colors"
                data-testid="nav-messages"
                aria-label={t("nav.messages")}
              >
                <MessageCircle className="h-4 w-4" />
                {unread > 0 && (
                  <span
                    className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-secondary text-secondary-foreground text-[10px] font-semibold ring-2 ring-background"
                    data-testid="messages-badge"
                  >
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </NavLink>
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu"
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground pl-1 pr-2 md:pr-3 py-1 text-sm hover:bg-primary/90 transition-colors"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary-foreground text-primary font-semibold">
                    {(user.name || user.email)[0]?.toUpperCase()}
                  </span>
                  <span className="hidden sm:inline max-w-[120px] truncate">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{user.name}</span>
                    <span className="text-xs text-muted-foreground">{user.email}</span>
                    <span className="text-[10px] mt-1 uppercase tracking-widest text-secondary">{user.role}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "agente" ? (
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> {t("nav.dashboard")}
                  </DropdownMenuItem>
                ) : user.role === "admin" ? (
                  <DropdownMenuItem onClick={() => navigate("/admin/news")} data-testid="menu-admin-news">
                    <Shield className="h-4 w-4 mr-2" /> Panel Admin
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => navigate("/favorites")} data-testid="menu-favorites">
                    <Heart className="h-4 w-4 mr-2" /> {t("nav.favorites")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-messages">
                  <MessageCircle className="h-4 w-4 mr-2" /> {t("nav.messages")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/cuenta")} data-testid="menu-account">
                  <KeyRound className="h-4 w-4 mr-2" /> Mi cuenta
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-foreground/70 hover:text-primary hidden sm:inline" data-testid="nav-login">
                {t("nav.login")}
              </Link>
              <Button asChild size="sm" className="rounded-full btn-hover-lift" data-testid="nav-register">
                <Link to="/register">{t("nav.register")}</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
